<template>
  <div class="app-detail-page">
    <PageHeader
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="app-detail-header">
          <AppLogoBadge
            :src="customerLogo"
            :alt="`Logo de ${customerName}`"
            :initials="customerInitials"
            variant="detail"
            aria-hidden="true"
          />

          <div class="app-detail-heading">
            <h2>{{ customerName }}</h2>
            <div class="app-detail-heading-meta">
              <span
                class="app-status-pill app-detail-status-pill"
                :class="customerIsActive ? 'app-status-active' : 'app-status-inactive'"
              >
                {{ customerIsActive ? text.table.status.active : text.table.status.inactive }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <template #actions="{ onAction }">
        <div class="app-page-header-actions app-detail-header-actions">
          <button
            v-for="action in primaryHeaderActions"
            :key="action.key"
            class="app-header-btn"
            :class="`app-header-btn--${action.variant || 'primary'}`"
            type="button"
            @click="handlePrimaryHeaderAction(action, onAction)"
          >
            <i v-if="action.icon" class="fa" :class="action.icon" aria-hidden="true"></i>
            <span>{{ action.label }}</span>
          </button>

          <div v-if="secondaryHeaderActions.length" class="app-detail-more-actions-wrap" ref="moreActionsWrap">
            <button
              type="button"
              class="app-header-btn app-header-btn--ghost app-detail-more-actions-btn"
              :title="text.actions.common.moreActions"
              @click.stop="toggleMoreActionsMenu"
            >
              <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
            </button>

            <div v-if="showMoreActionsMenu" class="app-detail-more-actions-menu">
              <button
                v-for="action in secondaryHeaderActions"
                :key="action.key"
                type="button"
                @click="handleSecondaryHeaderAction(action, onAction)"
              >
                {{ action.label }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </PageHeader>

    <div class="app-detail-content">
      <section v-if="loading" class="app-detail-loading card">
        <div class="card-body">{{ text.page.loadingDetail }}</div>
      </section>

      <template v-else>
        <section class="metrics-grid app-detail-kpis">
          <CustomersKpiCard
            v-for="metric in detailMetrics"
            :key="metric.key"
            :title="metric.title"
            :value="metric.value"
            :value-route="metric.valueRoute"
            :value-link-class="metric.valueLinkClass"
            :value-meta="metric.valueMeta"
            :truncate-value="metric.truncateValue"
            :value-scale="metric.valueScale"
            :icon="metric.icon"
            :icon-bg="metric.iconBg"
            :icon-color="metric.iconColor"
          />
        </section>

        <AppSectionTabs
          v-model="activeTab"
          :tabs="tabs"
          :aria-label="text.page.sectionTabsAriaLabel"
        />

        <CustomerDetailTabsPanel
          :active-tab="activeTab"
          :route-tab="$route?.query?.tab"
          :customer="customer"
          :customer-metrics="customerMetrics"
          :customer-overview="customerOverview"
          :persons="persons"
          :default-billing-data="defaultBillingData"
          @edit-section="openCustomerEditModal"
          @add-contact="openPersonCreateModal"
          @edit-contact="openPersonEditModal"
          @delete-contact="handleDeleteContact"
          @edit-billing-data="handleEditBillingData"
          @open-billing-data-list="openContributorListModal"
          @create-billing-data="handleCreateBillingData"
          @view-sale-detail="handleViewSaleDetail"
          @change-sale-status="handleChangeSaleStatus"
          @open-permissions="handleOpenCustomerPermissions"
          @open-merge="handleOpenCustomerMerge"
          @archive-toggle="handleArchiveCustomerToggle"
          @delete-customer="handleDeleteCustomerFromAdvancedOptions"
          @open-history="openHistoryTab"
        />
      </template>
    </div>

    <CustomerModal
      :show="isCustomerEditModalOpen"
      mode="edit"
      :initial-data="customer"
      :initial-section-key="customerEditSectionKey"
      @close="closeCustomerEditModal"
      @delete="handleDeleteCustomerFromModal"
      @submit="submitCustomerEdit"
    />

    <SaleModal
      :show="showCreateSaleModal"
      :mode="saleModalMode"
      :initial-data="saleModalInitialData"
      :sale-kind="saleModalKind"
      :service-filter-mode="saleModalServiceFilterMode"
      :advertising-enabled="isAdvertisingModuleActive"
      :submitting="createSaleSubmitting"
      @close="closeCreateSaleModal"
      @submit="submitCreateSale"
    />

    <PersonModal
      :show="isPersonModalOpen"
      :mode="personModalMode"
      :initial-data="personModalInitialData"
      :country-options="countryOptions"
      :state-options="stateOptions"
      :loading="isPersonSubmitting"
      @close="closePersonModal"
      @delete="handleDeleteContactFromModal"
      @submit="submitPerson"
    />

    <ContributorModal
      :show="isContributorModalOpen"
      :mode="contributorModalMode"
      :initial-data="contributorModalInitialData"
      :state-options="stateOptions"
      :regimen-options="regimenOptions"
      :uso-cfdi-options="usoCfdiOptions"
      :payment-method-options="paymentMethodOptions"
      :payment-method-type-options="paymentMethodTypeOptions"
      :show-delete-action="true"
      :loading="isContributorSubmitting"
      @close="closeContributorModal"
      @delete="handleDeleteContributorFromModal"
      @submit="submitContributor"
    />

    <ContributorListModal
      :show="isContributorListModalOpen"
      :contributors="contributors"
      @close="closeContributorListModal"
      @edit-contributor="handleEditContributorFromList"
      @delete-contributor="handleDeleteContributor"
    />

    <ConfirmActionModal
      :show="isConfirmActionModalOpen"
      :title="confirmActionTitle"
      :subtitle="confirmActionSubtitle"
      :message="confirmActionMessage"
      :confirm-text="confirmActionButton"
      :cancel-text="text.actions.common.cancel"
      @confirm="confirmPendingAction"
      @cancel="closeConfirmActionModal"
    />

    <CustomerVisibilityModal
      :show="isVisibilityModalOpen"
      :customer-name="customerName"
      :users-options="visibilityUsersOptions"
      :initial-visibility="customer?.visibility_settings?.mode || customer?.visibility || 'public'"
      :initial-private-user-ids="customer?.visibility_settings?.private_user_ids || []"
      :initial-hidden-user-ids="customer?.visibility_settings?.hidden_user_ids || []"
      :loading-users="visibilityUsersLoading"
      :submitting="visibilitySubmitting"
      @close="closeCustomerVisibilityModal"
      @submit="submitCustomerVisibility"
    />

    <CustomerMergeModal
      :show="isMergeModalOpen"
      :primary-customer="customer"
      :submitting="mergeSubmitting"
      @close="closeCustomerMergeModal"
      @submit="submitCustomerMerge"
    />
  </div>
</template>

<script>
import AppLogoBadge from '@/components/common/AppLogoBadge.vue';
import PageHeader from '@/components/common/PageHeader.vue';
import CustomersKpiCard from '@/components/customers/CustomersKpiCard.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import CustomerDetailTabsPanel from '@/components/customers/detail/CustomerDetailTabsPanel.vue';
import CustomerModal from '@/components/customers/CustomerModal.vue';
import SaleModal from '@/components/sales/SaleModal.vue';
import PersonModal from '@/components/people/PersonModal.vue';
import ContributorModal from '@/components/contributors/ContributorModal.vue';
import ContributorListModal from '@/components/contributors/ContributorListModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import CustomerVisibilityModal from '@/components/customers/detail/modals/CustomerVisibilityModal.vue';
import CustomerMergeModal from '@/components/customers/detail/modals/CustomerMergeModal.vue';
import {
  createCustomerDetailState,
  customerDetailComputed,
  customerDetailMethods,
} from '@/composables/customers/useCustomerDetailPage';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerDetailPage',
  components: {
    AppLogoBadge,
    PageHeader,
    CustomersKpiCard,
    AppSectionTabs,
    CustomerDetailTabsPanel,
    CustomerModal,
    SaleModal,
    PersonModal,
    ContributorModal,
    ContributorListModal,
    ConfirmActionModal,
    CustomerVisibilityModal,
    CustomerMergeModal,
  },
  data() {
    return {
      text,
      ...createCustomerDetailState(),
      showMoreActionsMenu: false,
    };
  },
  computed: {
    ...customerDetailComputed,
    primaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const preferredOrder = ['edit-customer', 'invoice-customer'];

      return preferredOrder
        .map((key) => actions.find((action) => String(action?.key || '') === key))
        .filter(Boolean);
    },
    secondaryHeaderActions() {
      const actions = Array.isArray(this.headerActions) ? this.headerActions : [];
      const primaryKeys = new Set(this.primaryHeaderActions.map((action) => String(action?.key || '')));

      return actions.filter((action) => !primaryKeys.has(String(action?.key || '')));
    },
    isAdvertisingModuleActive() {
      const can = typeof this.$can === 'function' ? this.$can : null;
      if (!can) {
        return true;
      }

      return can('sales.advertising');
    },
  },
  mounted() {
    this.syncActiveTabFromRoute();
    this.fetchCustomerDetail();
    document.addEventListener('click', this.handleHeaderDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleHeaderDocumentClick);
  },
  watch: {
    customerId(nextId, previousId) {
      if (nextId !== previousId) {
        this.fetchCustomerDetail();
      }
    },
    '$route.query.tab': {
      handler() {
        this.syncActiveTabFromRoute();
      },
      immediate: false,
    },
  },
  methods: {
    ...customerDetailMethods,
    syncActiveTabFromRoute() {
      const requestedTab = String(this.$route?.query?.tab || '').trim();
      if (!requestedTab) {
        return;
      }

      const legacyPrimaryTabMap = {
        sales: 'business',
        retainers: 'business',
        'multi-customer': 'business',
        multicliente: 'business',
        invoices: 'finance',
        'credit-notes': 'finance',
        'simple-notes': 'finance',
        payments: 'finance',
        'account-statement': 'analysis',
        'cash-flow': 'analysis',
      };

      const normalizedTab = legacyPrimaryTabMap[requestedTab] || requestedTab;

      const exists = Array.isArray(this.tabs)
        && this.tabs.some((tab) => String(tab?.key || '') === normalizedTab);

      if (!exists) {
        return;
      }

      this.activeTab = normalizedTab;
    },
    handlePrimaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction(action);
    },
    toggleMoreActionsMenu() {
      this.showMoreActionsMenu = !this.showMoreActionsMenu;
    },
    handleSecondaryHeaderAction(action, onAction) {
      this.showMoreActionsMenu = false;
      onAction(action);
    },
    handleHeaderDocumentClick(event) {
      const target = event?.target;
      if (target && target.closest('.app-detail-more-actions-wrap')) {
        return;
      }

      this.showMoreActionsMenu = false;
    },
    openHistoryTab() {
      this.activeTab = 'history';
    },
  },
};
</script>
